import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Edit2, Trash2, Users, X, Filter,
    RefreshCcw, MoreHorizontal, Layers,
    ChevronRight, CheckCircle2
} from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmContext';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import DataTable from './ui/DataTable';
import EmptyState from './ui/EmptyState';
import { Button } from './ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';


export default function Groups() {
    const navigate = useNavigate();
    const confirm = useConfirm();

    // ─── Index state ──────────────────────────────────────
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // ─── Create/Edit modal ────────────────────────────────
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState('STATIC');
    const [ruleConfig, setRuleConfig] = useState({ filters: [] });
    const [previewCount, setPreviewCount] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // ═══ GROUPS LIST ═════════════════════════════════════

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/groups', {
                params: { page, limit, search: searchTerm, sortBy, sortOrder }
            });
            setGroups(res.data.data || []);
            setTotalPages(res.data.pagination?.pages || 1);
            setTotalRecords(res.data.pagination?.total || 0);
        } catch {
            toast.error('Failed to load groups');
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchTerm, sortBy, sortOrder]);

    useEffect(() => { fetchGroups(); }, [fetchGroups]);

    const handleSort = (field) => {
        if (sortBy === field) setSortOrder(p => p === 'asc' ? 'desc' : 'asc');
        else { setSortBy(field); setSortOrder('desc'); }
        setPage(1);
    };

    // ═══ OPEN GROUP DETAIL ════════════════════════════════

    const handleOpenGroup = (grp) => {
        navigate(`/groups/${grp._id}`);
    };

    // ═══ CREATE / EDIT / DELETE ════════════════════════════

    const openCreateModal = () => {
        setIsEditing(false); setSelectedGroup(null);
        setName(''); setDescription(''); setType('STATIC');
        setRuleConfig({ filters: [] }); setPreviewCount(null);
        setIsModalOpen(true);
    };

    const openEditModal = (grp, e) => {
        e?.stopPropagation();
        setIsEditing(true); setSelectedGroup(grp);
        setName(grp.name); setDescription(grp.description || '');
        setType(grp.type || 'STATIC');
        setRuleConfig(grp.ruleConfig || { filters: [] });
        setPreviewCount(null);
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!name.trim()) { toast.warning('Group name is required'); return; }
        setIsSaving(true);
        try {
            if (isEditing) {
                await api.put(`/api/groups/${selectedGroup._id}`, { name, description, type, ruleConfig });
            } else {
                await api.post('/api/groups', { name, description, type, ruleConfig });
            }
            setIsModalOpen(false);
            fetchGroups();
            toast.success(isEditing ? 'Group updated' : 'Group created');
        } catch (err) {
            toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to save group');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (grp, e) => {
        e?.stopPropagation();
        const ok = await confirm(
            'Delete Group?',
            `Delete "${grp.name}"? This removes all memberships but DOES NOT delete the contacts themselves.`
        );
        if (!ok) return;
        try {
            await api.delete(`/api/groups/${grp._id}`);
            fetchGroups();
            toast.success('Group deleted');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete group');
        }
    };

    // Dynamic rule helpers
    const addFilter = () => setRuleConfig(p => ({ ...p, filters: [...p.filters, { field: 'stage', operator: 'equals', value: '' }] }));
    const updateFilter = (i, k, v) => {
        const f = [...ruleConfig.filters];
        f[i] = { ...f[i], [k]: v };
        setRuleConfig({ ...ruleConfig, filters: f });
    };
    const removeFilter = (i) => setRuleConfig(p => ({ ...p, filters: p.filters.filter((_, idx) => idx !== i) }));

    const handlePreviewCount = async () => {
        if (previewLoading) return; // debounce double-click
        setPreviewLoading(true);
        setPreviewCount(null);
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000));
        try {
            const res = await Promise.race([
                api.post('/api/groups/preview-count', { ruleConfig }),
                timeout
            ]);
            setPreviewCount(res.data.count ?? 0);
        } catch (err) {
            setPreviewCount(err.message === 'timeout' ? 'Timeout — query too heavy' : 'Error');
        } finally {
            setPreviewLoading(false);
        }
    };

    // ═══ RENDER ════════════════════════════════════════════


    // ── Index view ──────────────────────────────────────
    return (
        <PageShell>
            <PageHeader
                title="Groups"
                subtitle="Define reusable audience segments for sequences and campaigns."
                icon={Users}
                actions={
                    <Button onClick={openCreateModal} className="h-10 px-5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Create Group
                    </Button>
                }
            />

            <div className="bg-card border border-border rounded-[2.5rem] shadow-sm overflow-hidden">
                <DataTable
                    columns={[
                        {
                            header: 'Group',
                            accessor: 'name',
                            sortable: true,
                            render: (grp) => (
                                <div
                                    onClick={() => handleOpenGroup(grp)}
                                    className="cursor-pointer group/name flex flex-col"
                                >
                                    <span className="font-bold text-sm text-foreground group-hover/name:text-primary transition-colors">{grp.name}</span>
                                    {grp.description && (
                                        <span className="text-[11px] text-muted-foreground font-medium mt-0.5 truncate max-w-[240px]">{grp.description}</span>
                                    )}
                                </div>
                            )
                        },
                        {
                            header: 'Type',
                            accessor: 'type',
                            render: (grp) => (
                                <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${grp.type === 'DYNAMIC' ? 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' : 'bg-muted text-muted-foreground border-border'}`}>
                                    {grp.type === 'DYNAMIC' ? <><Filter className="w-2.5 h-2.5 inline mr-1" />Dynamic</> : <><Layers className="w-2.5 h-2.5 inline mr-1" />Static</>}
                                </span>
                            )
                        },
                        {
                            header: 'Members',
                            accessor: 'memberCount',
                            render: (grp) => (
                                <div className="flex items-center gap-1.5 text-sm font-black">
                                    <Users className="w-3.5 h-3.5 text-muted-foreground" />
                                    {grp.memberCount ?? 0}
                                </div>
                            )
                        },
                        {
                            header: 'Created',
                            accessor: 'createdAt',
                            sortable: true,
                            render: (grp) => (
                                <span className="text-[11px] text-muted-foreground font-medium">
                                    {new Date(grp.createdAt).toLocaleDateString()}
                                </span>
                            )
                        },
                        {
                            header: '',
                            accessor: '_id',
                            align: 'right',
                            render: (grp) => (
                                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="sm" variant="ghost" onClick={() => handleOpenGroup(grp)}
                                        className="h-8 px-3 rounded-xl font-black text-[10px] uppercase tracking-widest">
                                        Open <ChevronRight className="w-3 h-3 ml-1" />
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-xl">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-44 rounded-2xl shadow-xl">
                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-black">Actions</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={(e) => openEditModal(grp, e)} className="cursor-pointer font-bold text-xs rounded-xl py-2">
                                                <Edit2 className="w-3.5 h-3.5 mr-2" /> Edit Group
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => handleDelete(grp, e)} className="cursor-pointer font-bold text-xs rounded-xl py-2 text-destructive focus:text-destructive focus:bg-destructive/10">
                                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Group
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            )
                        }
                    ]}
                    data={groups}
                    loading={loading}
                    pagination={{ page, limit, totalRecords, totalPages }}
                    onPageChange={setPage}
                    onLimitChange={(v) => { setLimit(v); setPage(1); }}
                    sort={{ sortBy, sortOrder }}
                    onSort={handleSort}
                    search={{
                        placeholder: 'Search groups...',
                        value: searchTerm,
                        onChange: (val) => { setSearchTerm(val); setPage(1); }
                    }}
                    emptyState={
                        <EmptyState
                            title="No groups yet"
                            description="Groups are reusable audience segments. Create one to target contacts across sequences and campaigns."
                            icon={Users}
                            action={
                                <button onClick={openCreateModal} className="text-primary font-black text-sm hover:underline">Create your first group →</button>
                            }
                        />
                    }
                />
            </div>

            {isModalOpen && <GroupFormModal {...{ isEditing, name, setName, description, setDescription, type, setType, ruleConfig, addFilter, updateFilter, removeFilter, handlePreviewCount, previewLoading, previewCount, isSaving, handleSave, onClose: () => setIsModalOpen(false) }} />}
        </PageShell>
    );
}

// ═══ GROUP FORM MODAL ═════════════════════════════════════
function GroupFormModal({ isEditing, name, setName, description, setDescription, type, setType, ruleConfig, addFilter, updateFilter, removeFilter, handlePreviewCount, previewLoading, previewCount, isSaving, handleSave, onClose }) {
    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-card border-2 border-border/40 w-full max-w-md rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-400 flex flex-col max-h-[90vh]">
                <div className="p-7 border-b border-border/40 flex items-center justify-between bg-muted/10">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                            <Users className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black tracking-tight">{isEditing ? 'Edit Group' : 'Create Group'}</h3>
                            <p className="text-muted-foreground text-[10px] uppercase font-black tracking-widest mt-0.5">Audience Segment</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all group">
                        <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                    </button>
                </div>

                <div className="p-7 space-y-5 overflow-y-auto flex-1">
                    {/* Name */}
                    <div>
                        <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Group Name <span className="text-destructive">*</span></label>
                        <input
                            type="text" value={name} autoFocus
                            onChange={e => setName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSave()}
                            placeholder="e.g. SaaS Founders Q3"
                            className="w-full bg-muted/30 border-2 border-border/50 text-foreground rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all outline-none text-sm"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Description</label>
                        <textarea
                            value={description} rows={2}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="What's this group for?"
                            className="w-full bg-muted/30 border-2 border-border/50 text-foreground rounded-2xl focus:border-primary font-medium py-3.5 px-5 transition-all outline-none text-sm resize-none"
                        />
                    </div>

                    {/* Type */}
                    <div>
                        <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Group Type</label>
                        <div className="flex gap-2 p-1.5 bg-muted/30 border-2 border-border/50 rounded-2xl">
                            {[
                                { id: 'STATIC', label: 'Static', icon: <Layers className="w-3.5 h-3.5" />, desc: 'Manually managed members' },
                                { id: 'DYNAMIC', label: 'Dynamic', icon: <Filter className="w-3.5 h-3.5" />, desc: 'Rule-driven membership' }
                            ].map(t => (
                                <button key={t.id} onClick={() => setType(t.id)}
                                    className={`flex-1 flex flex-col items-center py-3 px-2 rounded-xl transition-all ${type === t.id ? 'bg-background text-primary shadow-sm border border-primary/10' : 'text-muted-foreground hover:text-foreground'}`}>
                                    <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-0.5">{t.icon} {t.label}</span>
                                    <span className="text-[9px] opacity-60">{t.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dynamic rules */}
                    {type === 'DYNAMIC' && (
                        <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                            <div className="flex items-center justify-between">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Rules</label>
                                <button onClick={addFilter} className="text-primary text-[10px] font-black uppercase tracking-widest flex items-center gap-1 hover:underline">
                                    <Plus className="w-3 h-3" /> Add Rule
                                </button>
                            </div>

                            <div className="space-y-2 max-h-[180px] overflow-y-auto">
                                {ruleConfig.filters.length === 0 ? (
                                    <div className="p-4 border-2 border-dashed border-border/50 rounded-2xl text-center">
                                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">No rules — matches all workspace contacts</p>
                                    </div>
                                ) : (
                                    ruleConfig.filters.map((f, i) => (
                                        <div key={i} className="flex gap-2 items-center bg-muted/20 p-3 rounded-2xl border border-border/50">
                                            {/* Field */}
                                            <Select value={f.field} onValueChange={v => updateFilter(i, 'field', v)}>
                                                <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold focus:border-primary">
                                                    <SelectValue placeholder="Field" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                    <SelectItem value="stage" className="font-bold text-xs cursor-pointer">Stage</SelectItem>
                                                    <SelectItem value="status" className="font-bold text-xs cursor-pointer">Status</SelectItem>
                                                    <SelectItem value="tags" className="font-bold text-xs cursor-pointer">Tags</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {/* Operator */}
                                            <Select value={f.operator} onValueChange={v => updateFilter(i, 'operator', v)}>
                                                <SelectTrigger className="w-20 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold focus:border-primary">
                                                    <SelectValue placeholder="Op" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                                                    <SelectItem value="equals" className="font-bold text-xs cursor-pointer">= equals</SelectItem>
                                                    <SelectItem value="not_equals" className="font-bold text-xs cursor-pointer">≠ not</SelectItem>
                                                    <SelectItem value="contains" className="font-bold text-xs cursor-pointer">~ contains</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {/* Value */}
                                            {f.field === 'stage' ? (
                                                <Select value={f.value} onValueChange={v => updateFilter(i, 'value', v)}>
                                                    <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold focus:border-primary">
                                                        <SelectValue placeholder="Value" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[300]">
                                                        <SelectItem value="LEAD" className="font-bold text-xs cursor-pointer">Lead</SelectItem>
                                                        <SelectItem value="CONTACTED" className="font-bold text-xs cursor-pointer">Contacted</SelectItem>
                                                        <SelectItem value="REPLIED" className="font-bold text-xs cursor-pointer">Replied</SelectItem>
                                                        <SelectItem value="MEETING_BOOKED" className="font-bold text-xs cursor-pointer">Meeting Booked</SelectItem>
                                                        <SelectItem value="CLOSED_WON" className="font-bold text-xs cursor-pointer">Won</SelectItem>
                                                        <SelectItem value="CLOSED_LOST" className="font-bold text-xs cursor-pointer">Lost</SelectItem>
                                                        <SelectItem value="UNQUALIFIED" className="font-bold text-xs cursor-pointer">Unqualified</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ) : f.field === 'status' ? (
                                                <Select value={f.value} onValueChange={v => updateFilter(i, 'value', v)}>
                                                    <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold focus:border-primary">
                                                        <SelectValue placeholder="Value" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[300]">
                                                        <SelectItem value="VERIFIED" className="font-bold text-xs cursor-pointer">Verified</SelectItem>
                                                        <SelectItem value="RISKY" className="font-bold text-xs cursor-pointer">Risky</SelectItem>
                                                        <SelectItem value="INVALID" className="font-bold text-xs cursor-pointer">Invalid</SelectItem>
                                                        <SelectItem value="UNKNOWN" className="font-bold text-xs cursor-pointer">Unknown</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={f.value}
                                                    onChange={e => updateFilter(i, 'value', e.target.value)}
                                                    placeholder="Value"
                                                    className="flex-1 h-9 bg-background border-2 border-border/50 text-xs font-bold px-3 rounded-xl focus:border-primary outline-none transition-colors"
                                                />
                                            )}
                                            <button onClick={() => removeFilter(i)} className="text-muted-foreground hover:text-destructive shrink-0 p-1.5 hover:bg-destructive/10 rounded-lg transition-colors">
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Preview count */}
                            <button onClick={handlePreviewCount}
                                className="w-full flex items-center justify-between bg-primary/5 border border-primary/20 rounded-2xl px-5 py-3 hover:bg-primary/10 transition-colors">
                                <span className="text-[10px] font-black text-primary uppercase tracking-widest flex items-center gap-2">
                                    <Users className="w-3.5 h-3.5" /> Preview Match Count
                                </span>
                                {previewLoading ? (
                                    <RefreshCcw className="w-3.5 h-3.5 text-primary animate-spin" />
                                ) : (
                                    <span className="text-sm font-black text-primary">{previewCount !== null ? previewCount : '...'}</span>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="p-7 border-t border-border/40 bg-muted/10 flex gap-4">
                    <Button variant="ghost" onClick={onClose} className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 border-border/40 hover:bg-background">
                        Cancel
                    </Button>
                    <Button disabled={isSaving || !name.trim()} onClick={handleSave}
                        className="flex-[2] h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center justify-center gap-2">
                        {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Group')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

